import type { OnboardingStepProps } from "./Onboarding";
import type { SetupOption } from "./OnboardingSetupShopContent";

import React from "react";

import { useShopifyIntegrationConnect } from "@editor/components/editor/page/useShopifyIntegrationConnect";
import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useLocalStorageState } from "@editor/hooks/useLocalStorage";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";

import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

import OnboardingFooter from "./OnboardingFooter";
import OnboardingSetupShopContent from "./OnboardingSetupShopContent";
import useConnectShopifyIntegrationOnboarding from "./useConnectShopifyIntegrationOnboarding";

const OnboardingSetupShop: React.FC<OnboardingStepProps> = ({
  currentStep,
  submitStep,
  isLoading,
  totalSteps,
}) => {
  const navigate = useNavigate();
  const logEvent = useLogAnalytics();
  const { user } = useCurrentUser();
  const [searchParams] = useSearchParams();

  const hasShopifyParams = React.useMemo(() => {
    const shopifyIntegrationId = searchParams.get("hmac");
    const pendingAppInstallationId = searchParams.get(
      "pendingAppInstallationId",
    );
    return Boolean(shopifyIntegrationId || pendingAppInstallationId);
  }, [searchParams]);

  const [selectedOption, setSelectedOption] = useLocalStorageState(
    "onboarding-setup-selected-option",
    hasShopifyParams ? "import" : null,
    {
      schema: z.union([z.literal("new"), z.literal("import"), z.null()]),
    },
  ) as [SetupOption | null, (value: SetupOption | null) => void];

  React.useEffect(() => {
    if (
      hasShopifyParams &&
      selectedOption !== "import" &&
      selectedOption === null
    ) {
      setSelectedOption("import");
    }
  }, [hasShopifyParams, selectedOption, setSelectedOption]);

  const { isChecking, isLoading: isLoadingShopifyIntegration } =
    useConnectShopifyIntegrationOnboarding({
      targetPath: "/onboarding/onboarding-design-library",
    });

  const workspaceId = useCurrentWorkspaceId();
  const { handleConnect, isLoading: isConnectingShopify } =
    useShopifyIntegrationConnect({
      workspaceId,
      cookieValue: { type: "onboarding" },
    });

  const handleSubmit = (e: React.MouseEvent) => {
    if (user && selectedOption) {
      logEvent("onboarding.flow.step.completed", {
        userId: user.email,
        stepId: "connect-store",
        stepType: "connect-store",
        stepData: {
          selectedOption,
        },
      });
    }

    if (selectedOption === "import") {
      void handleConnect(e);
    } else {
      submitStep();
    }
  };

  const handleBack = () => {
    navigate("/onboarding/onboarding-shop-info");
  };

  const isSubmitDisabled =
    isLoading ||
    isChecking ||
    isLoadingShopifyIntegration ||
    !selectedOption ||
    (selectedOption === "import" && isConnectingShopify);

  return (
    <div className="h-screen w-full flex flex-col">
      <div className="flex-1 overflow-y-auto flex flex-col items-center md:py-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="pt-14 md:pt-0 my-6 flex flex-col gap-1 text-center md:mb-35 md:gap-3">
          <h1 className="header-h1 text-3xl md:text-4xl">Connect Your Store</h1>
          <p className="text-muted header-h1 text-lg font-normal">
            Let's get you up and running!
          </p>
        </div>
        <div className="lg:w-[800px] w-full flex flex-col gap-10 pb-25 md:pb-40 lg:pb-40 px-5 lg:px-0">
          <OnboardingSetupShopContent
            selectedOption={selectedOption}
            setSelectedOption={(option) => setSelectedOption(option)}
            isShopContext={false}
          />
        </div>
      </div>

      <div className="absolute right-0 bottom-0 left-0 z-20">
        <OnboardingFooter
          currentStep={currentStep}
          onNext={handleSubmit}
          onBack={handleBack}
          className="w-full lg:max-w-[800px] px-5 lg:px-0"
          isNextLoading={
            isLoading || isConnectingShopify || isLoadingShopifyIntegration
          }
          isNextDisabled={isSubmitDisabled}
          totalSteps={totalSteps}
        />
      </div>
    </div>
  );
};

export default OnboardingSetupShop;
